import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
} from 'firebase/storage'
import { storage } from './config'

export const storageService = {
  // Check if a file exists in storage
  fileExists: async (path: string): Promise<boolean> => {
    try {
      const storageRef = ref(storage, path)
      await getMetadata(storageRef)
      return true
    } catch (error: any) {
      if (error.code === 'storage/object-not-found') {
        return false
      }
      // For other errors, throw them
      throw error
    }
  },

  // Upload a file
  uploadFile: async (path: string, file: File) => {
    const storageRef = ref(storage, path)
    const snapshot = await uploadBytes(storageRef, file)
    const downloadURL = await getDownloadURL(snapshot.ref)
    return downloadURL
  },

  // Get file URL with proper error handling for CORS and permissions
  getFileURL: async ({ path }: { path: string}) => {
    try {

      const storageRef = ref(storage, path)

      const downloadURL = await getDownloadURL(storageRef)
      return downloadURL

    } catch (error: any) {
      // Handle specific Firebase Storage errors
      if (error.code === 'storage/object-not-found') {
        throw new Error('File not found in storage')
      } else if (error.code === 'storage/unauthorized') {
        throw new Error('Unauthorized access to file. Please check storage rules.')
      } else if (error.code === 'storage/canceled') {
        throw new Error('File download was canceled')
      } else if (error.code === 'storage/unknown') {
        throw new Error('An unknown error occurred. Please check your network connection.')
      } else {
        throw new Error(error.message || 'Failed to get file URL')
      }
    }
  },

  // Delete a file
  deleteFile: async (path: string) => {
    const storageRef = ref(storage, path)
    await deleteObject(storageRef)
  },

  // List all files in a directory
  listFiles: async (path: string) => {
    const storageRef = ref(storage, path)
    const result = await listAll(storageRef)
    return result.items.map(item => item.fullPath)
  },

  // Upload a file with progress tracking
  uploadFileWithProgress: (
    path: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path)
      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          onProgress?.(progress)
        },
        (error) => reject(error),
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          resolve(downloadURL)
        }
      )
    })
  },
}
